/**
 * @file ReprendreSportApresOpHaut.tsx
 * @summary Article de blog — ReprendreSportApresOpHaut
 *
 * Page d'article TSX du silo SEO/GEO. Inclut métadonnées (SEO), JSON-LD, et CTA Resawod. Inscriptions cours via Resawod uniquement (sauf séances d'essai).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import ArticleLayout, {
  EnBref, QuandConsulter, AdaptationTable, FAQList, FurtherReading, SourcesSection, buildArticleJsonLd,
} from "@/components/blog/BlogParts";

const URL = "https://crossfitchistera.fr/blog/reprendre-sport-apres-operation-haut-du-corps";
const TITLE = "Reprendre le sport après une opération du haut du corps";
const DESC = "Épaule, coude, poignet : feuille de route concrète pour reprendre le sport après une chirurgie du haut du corps, en lien avec votre kiné.";

const author = { name: "Anthony Bendahmane (Benbou)", role: "Master STAPS, CrossFit Level 1" };
const reviewer = { name: "Cédric Bastien & Anthony Bendahmane", role: "Responsables de la publication – CrossFit Chistera" };

const faq = [
  { q: "Combien de temps avant de reprendre une activité comme le CrossFit ?", a: "Variable selon la chirurgie : 3 à 6 mois pour une coiffe, 6 à 9 mois pour une instabilité opérée, 4 à 6 mois après un coude. Le critère n'est pas le calendrier mais les feux verts kiné/chirurgien." },
  { q: "Puis-je commencer la box pendant la rééducation ?", a: "Oui, sur la partie bas du corps et cardio sans impact des bras. C'est même recommandé pour garder de la condition générale." },
  { q: "Le coach a-t-il besoin de mon compte rendu opératoire ?", a: "Pas du compte rendu, mais des consignes claires de votre chirurgien et kiné : amplitudes autorisées, charges, mouvements interdits, durée des restrictions." },
  { q: "Et si je n'ai plus de douleur ?", a: "L'absence de douleur ne signifie pas tissu cicatrisé. Respecter les délais biologiques (8 à 12 semaines minimum pour un tendon réinséré) reste indispensable." },
];

export default function ReprendreSportApresOpHaut() {
  return (
    <ArticleLayout
      url={URL} title={TITLE} description={DESC}
      h1="Reprendre le sport après une opération du haut du corps"
      breadcrumbLabel="Op. haut du corps"
      category="Reprise · Post-opératoire"
      author={author} reviewer={reviewer}
      publishedAt="2026-12-15" readingMinutes={11}
      ctaVariant="reprise" needsHealthDisclaimer
      jsonLd={buildArticleJsonLd({ url: URL, title: TITLE, description: DESC, publishedAt: "2026-12-15", author, reviewer, faq })}
    >
      <EnBref>
        <p>Reprendre le sport après une chirurgie de l'<strong>épaule, du coude ou du poignet</strong> demande une vraie feuille de route. Le bon timing dépend du geste opératoire, pas de votre calendrier. La règle d'or : <strong>travailler en lien avec votre kiné et votre chirurgien</strong>, et garder en permanence du travail sur le bas du corps et le cardio.</p>
      </EnBref>

      <QuandConsulter title="Avant de reprendre, valider avec votre équipe médicale">
        <ul>
          <li>Compte rendu opératoire et délais de cicatrisation des structures touchées</li>
          <li>Amplitudes articulaires autorisées (et celles à éviter, ex. abduction + rotation externe après stabilisation d'épaule)</li>
          <li>Charges autorisées et mouvements interdits sur les 3 à 6 prochains mois</li>
          <li>Critères pour passer à la phase suivante (force, contrôle, absence de douleur sous charge)</li>
        </ul>
      </QuandConsulter>

      <h2>Les 4 phases de reprise</h2>
      <ol>
        <li><strong>Protection (0 à 6 sem)</strong> — kiné, mobilité passive, bas du corps autorisé.</li>
        <li><strong>Réveil musculaire (6 à 12 sem)</strong> — isométrique, amplitudes contrôlées, charges très légères.</li>
        <li><strong>Réathlétisation (3 à 6 mois)</strong> — charges progressives, retour aux mouvements globaux scalés. C'est <Link to="/blog/reprendre-le-sport-apres-blessure">la phase trop souvent oubliée</Link>.</li>
        <li><strong>Retour aux WOD (à partir de 4 à 9 mois selon op)</strong> — mouvement standards réintroduits un par un, avec critères de progression.</li>
      </ol>

      <h2>Adaptations type pendant la reprise</h2>
      <AdaptationTable rows={[
        { standard: "Pull-ups, muscle-ups, kipping", adapted: "Tirage assis machine, ring rows à amplitude limitée" },
        { standard: "Push-press, jerk, snatch", adapted: "Push-up incliné, landmine press, développé technique léger" },
        { standard: "Toes-to-bar, hanging knee raises", adapted: "Crunch, dead bug, hollow hold" },
        { standard: "Burpees, handstand push-ups", adapted: "Burpees sans poussée, pike push-up box" },
        { standard: "Front squat, thrusters", adapted: "Goblet squat, back squat (selon op)" },
      ]} />

      <h2>Ce que nous faisons à la box</h2>
      <p>À votre arrivée, vous échangez avec un coach <em>avant</em> le premier WOD. On note vos restrictions, on construit un plan progressif, on adapte chaque séance. Le programme <Link to="/reprendre-le-sport">Reprendre le sport</Link> est conçu exactement pour ça. Pour un cadre encore plus précis, le coaching individuel permet une <Link to="/blog/coaching-individuel-objectifs-sportifs">progression sur mesure</Link>.</p>

      <FAQList items={faq} />

      <FurtherReading items={[
        { label: "Reprendre le sport après une blessure (pilier)", to: "/blog/reprendre-le-sport-apres-blessure" },
        { label: "Douleur à l'épaule en CrossFit", to: "/blog/douleur-epaule-crossfit" },
        { label: "Douleur au coude en CrossFit", to: "/blog/douleur-coude-crossfit" },
        { label: "Programme Reprendre le sport", to: "/reprendre-le-sport" },
      ]} />

      <SourcesSection items={[
        { label: "HAS — Recommandations sur la rééducation post-chirurgicale de l'épaule", href: "https://www.has-sante.fr/" },
        { label: "BJSM — Return to sport after shoulder surgery", href: "https://bjsm.bmj.com/" },
      ]} />
    </ArticleLayout>
  );
}
